import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Plus, Save, Trash2 } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import { api } from '../../../shared/lib/axios';
import type {
  ApprovalWorkflow,
  ApprovalWorkflowStep,
  ModoExecucaoWorkflow,
  TipoCondicaoStepAprovacao,
  TipoWorkflowProposta,
} from '../api';
import {
  useAdicionarWorkflowStep,
  useAtualizarWorkflow,
  useAtualizarWorkflowStep,
  useCriarWorkflow,
  useRemoverWorkflow,
  useRemoverWorkflowStep,
  useThresholds,
  useUpsertThreshold,
  useWorkflow,
  useWorkflows,
} from '../hooks';

const TIPO_OPTIONS: Array<{ value: TipoWorkflowProposta; label: string }> = [
  { value: 'default', label: 'Padrão' },
  { value: 'novo_negocio', label: 'Novo negócio' },
  { value: 'renovacao', label: 'Renovação' },
  { value: 'upsell', label: 'Upsell' },
  { value: 'cross_sell', label: 'Cross sell' },
  { value: 'licitacao', label: 'Licitação' },
];

const MODO_OPTIONS: Array<{ value: ModoExecucaoWorkflow; label: string }> = [
  { value: 'sequencial', label: 'Sequencial' },
  { value: 'paralelo', label: 'Paralelo' },
];

const CONDITION_OPTIONS: Array<{ value: TipoCondicaoStepAprovacao; label: string }> = [
  { value: 'always', label: 'Sempre' },
  { value: 'discount_above_limit', label: 'Desconto acima do limite' },
  { value: 'margin_below_min', label: 'Margem abaixo do mínimo' },
  { value: 'value_above', label: 'Valor total acima de' },
];

const DEFAULT_WORKFLOW_ID = '11111111-1111-4111-8111-111111111111';

/**
 * Admin de workflows de aprovação (US-COM-006, task 16.6 + mini-fatia 2).
 *
 * Interface em 3 painéis:
 *
 *   1. Lista de workflows à esquerda + botão "Novo".
 *   2. Detalhe do workflow selecionado à direita — dados básicos +
 *      steps (CRUD inline).
 *   3. Bloco de thresholds globais + preview (workflow_id +
 *      proposal_version_id → resolução simulada).
 *
 * Regras espelhadas do backend:
 *
 *   - Workflow padrão (`DEFAULT_WORKFLOW_ID`) não pode ser deletado nem
 *     desativado — a UI esconde os botões.
 *   - Steps têm ordem única por workflow; colisão vira 409 do backend
 *     e é exibida inline.
 *   - Cadeia sem steps ativos deixa propostas paralisadas em
 *     `em_aprovacao` — a UI destaca com Alert quando o admin remove
 *     todos os steps ativos de um workflow.
 */
export default function AdminProposalWorkflowsPage(): React.ReactElement {
  const navigate = useNavigate();
  const workflows = useWorkflows();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);

  return (
    <div className="p-4 sm:p-6">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar
      </button>

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Workflows de Aprovação</h1>
          <p className="mt-1 text-sm text-gray-600">
            CRUD dos workflows parametrizáveis + thresholds globais + preview de resolução.
          </p>
        </div>
        <Button onClick={() => setShowNewForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Novo workflow
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        <aside className="space-y-2">
          {workflows.isLoading && <Skeleton className="h-40 w-full" />}
          {workflows.isError && (
            <Alert variant="error">Erro ao carregar workflows.</Alert>
          )}
          {(workflows.data ?? []).map((wf) => (
            <button
              key={wf.id}
              type="button"
              onClick={() => setSelectedId(wf.id)}
              className={`w-full rounded border px-3 py-2 text-left text-sm ${
                selectedId === wf.id
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">{wf.nome}</span>
                {!wf.ativo && (
                  <span className="rounded bg-gray-200 px-1.5 py-0.5 text-xs text-gray-700">
                    inativo
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-xs text-gray-500">
                {wf.tipo} · {wf.modoExecucao}
              </div>
            </button>
          ))}
        </aside>

        <div className="space-y-6">
          {showNewForm && <NewWorkflowForm onClose={() => setShowNewForm(false)} onCreated={setSelectedId} />}
          {selectedId && <WorkflowDetalhe id={selectedId} onDeleted={() => setSelectedId(null)} />}
          <ThresholdsPanel />
          <PreviewPanel />
        </div>
      </div>
    </div>
  );
}

// ---- Novo workflow --------------------------------------------------

function NewWorkflowForm({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const criar = useCriarWorkflow();
  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState<TipoWorkflowProposta>('novo_negocio');
  const [modo, setModo] = useState<ModoExecucaoWorkflow>('sequencial');
  const [descricao, setDescricao] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  async function handleSubmit() {
    setErro(null);
    if (nome.trim().length < 3) return setErro('Nome deve ter ao menos 3 caracteres.');
    try {
      const wf = await criar.mutateAsync({
        nome: nome.trim(),
        tipo,
        modoExecucao: modo,
        descricao: descricao.trim() || undefined,
      });
      onCreated(wf.id);
      onClose();
    } catch (e) {
      setErro(extractError(e));
    }
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="mb-3 text-sm font-medium text-gray-900">Novo workflow</h3>
      {erro && <Alert variant="error" className="mb-3">{erro}</Alert>}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TextInput label="Nome *" value={nome} onChange={(e) => setNome(e.target.value)} />
        <AutocompleteSelect
          label="Tipo *"
          options={TIPO_OPTIONS}
          value={tipo}
          onChange={(v) => setTipo(v as TipoWorkflowProposta)}
        />
        <AutocompleteSelect
          label="Modo de execução"
          options={MODO_OPTIONS}
          value={modo}
          onChange={(v) => setModo(v as ModoExecucaoWorkflow)}
        />
        <div className="md:col-span-2">
          <Textarea
            label="Descrição"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            rows={2}
          />
        </div>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={criar.isPending}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} disabled={criar.isPending}>
          {criar.isPending ? 'Criando…' : 'Criar workflow'}
        </Button>
      </div>
    </section>
  );
}

// ---- Detalhe do workflow --------------------------------------------

function WorkflowDetalhe({ id, onDeleted }: { id: string; onDeleted: () => void }) {
  const detail = useWorkflow(id);
  const atualizar = useAtualizarWorkflow();
  const remover = useRemoverWorkflow();

  const isDefault = id === DEFAULT_WORKFLOW_ID;

  if (detail.isLoading) return <Skeleton className="h-40 w-full" />;
  if (detail.isError || !detail.data)
    return <Alert variant="error">Não foi possível carregar o workflow.</Alert>;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-900">
          {detail.data.workflow.nome}
          {isDefault && (
            <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">
              padrão
            </span>
          )}
        </h3>
        {!isDefault && (
          <Button
            variant="secondary"
            size="sm"
            onClick={async () => {
              if (!window.confirm(`Remover o workflow "${detail.data!.workflow.nome}"?`)) return;
              try {
                await remover.mutateAsync(id);
                onDeleted();
              } catch (e) {
                window.alert(extractError(e));
              }
            }}
            disabled={remover.isPending}
          >
            <Trash2 className="mr-1 h-4 w-4" />
            Remover
          </Button>
        )}
      </div>

      <WorkflowBasicoForm
        workflow={detail.data.workflow}
        isDefault={isDefault}
        onSave={(payload) => atualizar.mutateAsync({ id, payload })}
      />

      <StepsList workflowId={id} steps={detail.data.steps} />
    </section>
  );
}

function WorkflowBasicoForm({
  workflow,
  isDefault,
  onSave,
}: {
  workflow: ApprovalWorkflow;
  isDefault: boolean;
  onSave: (payload: {
    nome?: string;
    modoExecucao?: ModoExecucaoWorkflow;
    ativo?: boolean;
    descricao?: string;
  }) => Promise<unknown>;
}) {
  const [nome, setNome] = useState(workflow.nome);
  const [modo, setModo] = useState<ModoExecucaoWorkflow>(workflow.modoExecucao);
  const [ativo, setAtivo] = useState(workflow.ativo);
  const [descricao, setDescricao] = useState(workflow.descricao ?? '');
  const [erro, setErro] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setErro(null);
    setSaving(true);
    try {
      await onSave({
        nome,
        modoExecucao: modo,
        ativo,
        descricao: descricao.trim() || undefined,
      });
    } catch (e) {
      setErro(extractError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mb-4 space-y-3">
      {erro && <Alert variant="error">{erro}</Alert>}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TextInput label="Nome *" value={nome} onChange={(e) => setNome(e.target.value)} />
        <AutocompleteSelect
          label="Modo de execução"
          options={MODO_OPTIONS}
          value={modo}
          onChange={(v) => setModo(v as ModoExecucaoWorkflow)}
        />
      </div>
      <Textarea
        label="Descrição"
        value={descricao}
        onChange={(e) => setDescricao(e.target.value)}
        rows={2}
      />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300"
          checked={ativo}
          onChange={(e) => setAtivo(e.target.checked)}
          disabled={isDefault}
        />
        Workflow ativo{' '}
        {isDefault && (
          <span className="text-xs text-gray-500">
            (padrão — não pode ser desativado)
          </span>
        )}
      </label>
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          <Save className="mr-1 h-4 w-4" />
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </div>
    </div>
  );
}

// ---- Steps ----------------------------------------------------------

function StepsList({
  workflowId,
  steps,
}: {
  workflowId: string;
  steps: ApprovalWorkflowStep[];
}) {
  const remover = useRemoverWorkflowStep(workflowId);
  const [addingStep, setAddingStep] = useState(false);

  const stepsAtivos = steps.filter((s) => s.ativo);
  const semStepsAtivos = stepsAtivos.length === 0;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900">Steps ({steps.length})</h4>
        <Button size="sm" variant="secondary" onClick={() => setAddingStep(true)}>
          <Plus className="mr-1 h-4 w-4" />
          Adicionar step
        </Button>
      </div>

      {semStepsAtivos && !addingStep && (
        <Alert variant="warning" className="mb-3">
          Nenhum step ativo — propostas com este workflow ficarão paralisadas em Em Aprovação.
        </Alert>
      )}

      {addingStep && (
        <AddStepForm workflowId={workflowId} onClose={() => setAddingStep(false)} />
      )}

      {steps.length === 0 ? (
        <EmptyState title="Sem steps" description="Adicione a primeira etapa deste workflow." />
      ) : (
        <ol className="space-y-2">
          {[...steps]
            .sort((a, b) => a.ordem - b.ordem)
            .map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between rounded border border-gray-200 bg-gray-50 p-3 text-sm"
              >
                <div>
                  <div className="font-medium text-gray-900">
                    #{s.ordem} · {s.roleSlug}
                    {!s.ativo && (
                      <span className="ml-2 rounded bg-gray-200 px-1.5 py-0.5 text-xs text-gray-700">
                        inativo
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    Feature: {s.featureRequired} · Condição: {s.conditionType}
                    {s.conditionType === 'value_above' && s.conditionValue && (
                      <> · Valor: R$ {Number(s.conditionValue).toLocaleString('pt-BR')}</>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm(`Remover step #${s.ordem} (${s.roleSlug})?`)) return;
                    try {
                      await remover.mutateAsync(s.id);
                    } catch (e) {
                      window.alert(extractError(e));
                    }
                  }}
                  className="text-red-600 hover:text-red-800"
                  aria-label="Remover step"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
        </ol>
      )}
    </div>
  );
}

function AddStepForm({ workflowId, onClose }: { workflowId: string; onClose: () => void }) {
  const adicionar = useAdicionarWorkflowStep(workflowId);
  const [ordem, setOrdem] = useState('1');
  const [roleSlug, setRoleSlug] = useState('gestor_interno');
  const [feature, setFeature] = useState('proposal.aprovar');
  const [conditionType, setConditionType] = useState<TipoCondicaoStepAprovacao>('always');
  const [conditionValue, setConditionValue] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  async function handleSubmit() {
    setErro(null);
    const ordemN = Number(ordem);
    if (!Number.isInteger(ordemN) || ordemN < 1) return setErro('Ordem inválida.');
    if (conditionType === 'value_above' && !conditionValue) {
      return setErro('Valor da condição é obrigatório para "Valor acima de".');
    }
    try {
      await adicionar.mutateAsync({
        ordem: ordemN,
        roleSlug: roleSlug.trim(),
        featureRequired: feature.trim(),
        conditionType,
        conditionValue: conditionValue ? Number(conditionValue) : undefined,
      });
      onClose();
    } catch (e) {
      setErro(extractError(e));
    }
  }

  return (
    <div className="mb-3 rounded border border-blue-200 bg-blue-50 p-3">
      {erro && <Alert variant="error" className="mb-2">{erro}</Alert>}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <TextInput label="Ordem *" type="number" value={ordem} onChange={(e) => setOrdem(e.target.value)} />
        <TextInput label="Role slug *" value={roleSlug} onChange={(e) => setRoleSlug(e.target.value)} />
        <TextInput
          label="Feature required *"
          value={feature}
          onChange={(e) => setFeature(e.target.value)}
        />
        <AutocompleteSelect
          label="Condição *"
          options={CONDITION_OPTIONS}
          value={conditionType}
          onChange={(v) => setConditionType(v as TipoCondicaoStepAprovacao)}
        />
        {conditionType === 'value_above' && (
          <TextInput
            label="Valor (R$) *"
            type="number"
            value={conditionValue}
            onChange={(e) => setConditionValue(e.target.value)}
          />
        )}
      </div>
      <div className="mt-2 flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancelar
        </Button>
        <Button size="sm" onClick={handleSubmit} disabled={adicionar.isPending}>
          {adicionar.isPending ? 'Adicionando…' : 'Adicionar step'}
        </Button>
      </div>
    </div>
  );
}

// ---- Thresholds -----------------------------------------------------

function ThresholdsPanel() {
  const thresholds = useThresholds();
  const upsert = useUpsertThreshold();
  const [chave, setChave] = useState('margin_min_default_pct');
  const [valor, setValor] = useState('');
  const [descricao, setDescricao] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  async function handleUpsert() {
    setErro(null);
    if (!chave.trim()) return setErro('Chave é obrigatória.');
    if (!valor.trim()) return setErro('Valor é obrigatório.');
    try {
      await upsert.mutateAsync({
        chave: chave.trim(),
        payload: {
          valor: Number(valor),
          descricao: descricao.trim() || undefined,
        },
      });
      setValor('');
      setDescricao('');
    } catch (e) {
      setErro(extractError(e));
    }
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="mb-3 text-sm font-medium text-gray-900">Thresholds globais</h3>
      <ul className="mb-4 space-y-1 text-sm">
        {(thresholds.data ?? []).map((t) => (
          <li key={t.id} className="flex items-center justify-between rounded bg-gray-50 px-3 py-2">
            <div>
              <span className="font-mono text-xs text-gray-700">{t.chave}</span>
              {t.descricao && <p className="text-xs text-gray-500">{t.descricao}</p>}
            </div>
            <span className="font-medium text-gray-900">{t.valor}</span>
          </li>
        ))}
      </ul>

      {erro && <Alert variant="error" className="mb-2">{erro}</Alert>}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
        <TextInput label="Chave" value={chave} onChange={(e) => setChave(e.target.value)} />
        <TextInput
          label="Valor"
          type="number"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
        />
        <TextInput
          label="Descrição (opcional)"
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
        />
      </div>
      <div className="mt-2 flex justify-end">
        <Button size="sm" onClick={handleUpsert} disabled={upsert.isPending}>
          <Save className="mr-1 h-4 w-4" />
          {upsert.isPending ? 'Salvando…' : 'Salvar threshold'}
        </Button>
      </div>
    </section>
  );
}

// ---- Preview --------------------------------------------------------

function PreviewPanel() {
  const [workflowId, setWorkflowId] = useState('');
  const [proposalVersionId, setProposalVersionId] = useState('');
  const [result, setResult] = useState<unknown>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function runPreview() {
    setResult(null);
    setErro(null);
    if (!workflowId.trim() || !proposalVersionId.trim()) {
      setErro('Informe workflow_id e proposal_version_id.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/proposal-workflows/${workflowId}/preview?proposal_version_id=${proposalVersionId}`,
      );
      setResult(data);
    } catch (e) {
      setErro(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="mb-3 text-sm font-medium text-gray-900">Preview de resolução</h3>
      <p className="mb-3 text-xs text-gray-600">
        Simula a resolução do workflow para uma versão de proposta sem gravar nada — mitiga
        erro de configuração antes de ativar em produção.
      </p>
      {erro && <Alert variant="error" className="mb-2">{erro}</Alert>}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <TextInput
          label="workflow_id"
          value={workflowId}
          onChange={(e) => setWorkflowId(e.target.value)}
        />
        <TextInput
          label="proposal_version_id"
          value={proposalVersionId}
          onChange={(e) => setProposalVersionId(e.target.value)}
        />
      </div>
      <div className="mt-2">
        <Button size="sm" onClick={runPreview} disabled={loading}>
          <Eye className="mr-1 h-4 w-4" />
          {loading ? 'Simulando…' : 'Simular'}
        </Button>
      </div>
      {result !== null && (
        <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-gray-50 p-3 text-xs text-gray-800">
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
    </section>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const resp = (e as { response?: { data?: { message?: string | string[] } } }).response;
    const msg = resp?.data?.message;
    if (Array.isArray(msg)) return msg.join('; ');
    if (typeof msg === 'string') return msg;
  }
  return e instanceof Error ? e.message : 'Erro desconhecido.';
}

export { AdminProposalWorkflowsPage };
